// HTTP transport core for the fetch-based API client (see utils/api.ts).
// Knows about URLs, auth headers, and the 401→refresh retry; nothing about
// React or specific endpoints — that lives in ApiClient / feature services.
import { authStore, refreshAccessToken } from '@/store/auth'

export const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || '/api'

// `error` stays a stable machine-readable code. When an older backend has
// not started returning `message` yet, keep that code from leaking into the
// customer-facing toast.
const API_ERROR_MESSAGES: Record<string, string> = {
  invalid_status: "That status is not available for this project's workflow. Refresh the task and choose one of its current statuses.",
}

function getAuthHeaders(): HeadersInit {
  const token = authStore.getState().accessToken
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  }
}

export async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry: boolean = false
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  const headers = new Headers(getAuthHeaders())
  if (options.headers) {
    new Headers(options.headers).forEach((value, key) => headers.set(key, value))
  }
  // A multipart upload must NOT carry the JSON content type getAuthHeaders()
  // sets: the browser has to write `multipart/form-data; boundary=…` itself,
  // and an explicit header overrides it with one that has no boundary, so the
  // server sees an unparseable body. Deleting it lets fetch fill it in.
  if (options.body instanceof FormData) headers.delete('Content-Type')

  const config: RequestInit = {
    credentials: 'include', // Important for cookies
    ...options,
    headers,
  }

  const response = await fetch(url, config)

  if (!response.ok) {
    // Handle 401 Unauthorized - try to refresh token.
    // Routes through the shared single-flight refresh so N parallel 401s
    // coalesce into ONE /auth/refresh (rotation makes concurrent refreshes
    // revoke each other → spurious logout).
    if (response.status === 401 && !isRetry && !endpoint.includes('/auth/refresh')) {
      const newToken = await refreshAccessToken()
      if (newToken) {
        // Retry the original request with the new token.
        return request<T>(endpoint, options, true)
      }
      // Refresh failed — no valid session.
      authStore.getState().clear()
      throw new Error('Authentication expired')
    }

    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
    const errorCode = typeof errorData.error === 'string' ? errorData.error : undefined
    const message = (
      (typeof errorData.message === 'string' && errorData.message) ||
      (errorCode && API_ERROR_MESSAGES[errorCode]) ||
      errorCode ||
      `HTTP ${response.status}`
    )
    throw new Error(message)
  }

  // 204 No Content (hard deletes) has an empty body — .json() would throw.
  if (response.status === 204) {
    return undefined as T
  }
  return response.json()
}
